import type { DashboardAntwort } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { downloadFile } from '@/lib/download'
import type { ExportRequest } from '@/lib/export-request'
import type {
  CalcEvent,
  CalcFile,
  CalcLookupClient,
  CalcLookupAddress,
  CalcLookupContact,
  CalcLookupUser,
  CalcScheme,
  Calculation,
  CalculationListRow,
  CalculationNeighbors,
  ListCalculationsParams,
} from '../types/calculation.types'

/**
 * AP-37 8. — die Kalkulation über die API (`/calculations`, Gate
 * `CALCULATION.CORE`). Auswahlhilfen unter `/calculations/lookups`.
 */

export interface PaginatedCalculations {
  data: CalculationListRow[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export const fetchCalculations = (params: ListCalculationsParams) =>
  api.get<PaginatedCalculations>('/calculations', { params }).then((r) => r.data)

export const fetchCalculation = (id: string) =>
  api.get<{ data: Calculation }>(`/calculations/${id}`).then((r) => r.data.data)

export interface CreateCalculationPayload {
  name: string
  clientId?: string
  prospectName?: string
  prospectCity?: string
  contactPersonId?: string
  siteName?: string
  dueAt?: string
  validUntil?: string
  ownerUserId?: string
  schemeId?: string
  isTemplate?: boolean
}

export const createCalculation = (dto: CreateCalculationPayload) =>
  api.post<{ data: Calculation }>('/calculations', dto).then((r) => r.data.data)

export const changeCalculationStatus = (id: string, dto: { status: string; lostReasonKey?: string; lostNote?: string }) =>
  api.post<{ data: Calculation }>(`/calculations/${id}/status`, dto).then((r) => r.data.data)

export const submitCalculation = (id: string) =>
  api.post<{ data: Calculation }>(`/calculations/${id}/submit`).then((r) => r.data.data)

export const newRevision = (id: string) =>
  api.post<{ data: Calculation }>(`/calculations/${id}/revision`).then((r) => r.data.data)

export const copyCalculation = (id: string, dto: { asTemplate?: boolean; name?: string }) =>
  api.post<{ data: Calculation }>(`/calculations/${id}/copy`, dto).then((r) => r.data.data)

export const fetchCalculationEvents = (id: string) =>
  api.get<{ data: CalcEvent[] }>(`/calculations/${id}/events`).then((r) => r.data.data)

export const fetchCalculationNeighbors = (id: string, params: ListCalculationsParams) =>
  api.get<{ data: CalculationNeighbors }>(`/calculations/${id}/neighbors`, { params }).then((r) => r.data.data)

export const calculationsExportRequest = (
  params: ListCalculationsParams & { format: string; orientation?: string },
  columns: { key: string; label: string }[],
): ExportRequest => ({
  url: '/calculations/export',
  params: { ...params, columns: JSON.stringify(columns) },
})

// ── Kalkulationsverzeichnis (4.18.1) ─────────────────────────────────────────

/** Detailblatt O 4a: wohin eine Datei gehört — an eine Position oder an eine ihrer Materialzeilen. */
export interface DateiZiel {
  positionId?: string | null
  costLineId?: string | null
}

export const fetchCalculationFiles = (id: string, filter: DateiZiel & { assigned?: boolean } = {}) =>
  api
    .get<{ data: CalcFile[] }>(`/calculations/${id}/files`, {
      params: {
        ...(filter.positionId ? { positionId: filter.positionId } : {}),
        ...(filter.costLineId ? { costLineId: filter.costLineId } : {}),
        ...(filter.assigned !== undefined ? { assigned: String(filter.assigned) } : {}),
      },
    })
    .then((r) => r.data.data)

export const uploadCalculationFile = (id: string, datei: File, ziel: DateiZiel = {}) => {
  const form = new FormData()
  form.append('file', datei)
  if (ziel.costLineId) form.append('costLineId', ziel.costLineId)
  else if (ziel.positionId) form.append('positionId', ziel.positionId)
  return api.post<{ data: CalcFile }>(`/calculations/${id}/files`, form).then((r) => r.data.data)
}

export const deleteCalculationFile = (id: string, fid: string) => api.delete(`/calculations/${id}/files/${fid}`)

/**
 * Eine Datei, die das Backend nur mit Anmeldung ausliefert (Muster AP-35
 * `oeffneGeschuetzt`): Das Zugriffstoken steht im Speicher, ein schlichter Link
 * bekäme 401.
 */
export async function oeffneKalkulationsdatei(id: string, fid: string, name: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/calculations/${id}/files/${fid}/content`, { responseType: 'arraybuffer' })
  const mime = String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0]
  downloadFile(res.data, name, mime)
}

// ── Auswahlhilfen ────────────────────────────────────────────────────────────

export const lookupUsers = () => api.get<{ data: CalcLookupUser[] }>('/calculations/lookups/users').then((r) => r.data.data)
export const lookupClients = (q?: string) =>
  api.get<{ data: CalcLookupClient[] }>('/calculations/lookups/clients', { params: { q } }).then((r) => r.data.data)
export const lookupContacts = (clientId: string) =>
  api.get<{ data: CalcLookupContact[] }>(`/calculations/lookups/clients/${clientId}/contacts`).then((r) => r.data.data)
export const lookupAddresses = (clientId: string) =>
  api.get<{ data: CalcLookupAddress[] }>(`/calculations/lookups/clients/${clientId}/addresses`).then((r) => r.data.data)
export const fetchSchemes = () => api.get<{ data: CalcScheme[] }>('/calculation-settings/schemes').then((r) => r.data.data)

/** AP-40 7.2 — alle Kacheln des Kalkulations-Dashboards in einem Aufruf. */
export const fetchCalculationDashboard = (scope: 'all' | 'mine') =>
  api.get<{ data: DashboardAntwort }>('/calculations/dashboard', { params: { scope } }).then((r) => r.data.data)
